import React, { useEffect, useMemo, useState } from 'react';

import PropTypes from 'prop-types';
import { toast } from 'sonner';
import Honeybadger from '@honeybadger-io/js';

import { useAuth } from '../../../../../shared/hooks';
import { deserializer } from '../../../../../shared/utils';
import { fetchTags as apiFetchTags } from '../../../../shared/services';
import { withCurrentUserFirst } from '../../../../shared/utils';
import { fetchAppointments as apiFetchAppointments } from '../../services/appointmentService';
import { fetchUsers as apiFetchUsers } from '../../services/userService';
import CalendarContext from './CalendarContext';

const CalendarProvider = ({ children }) => {
  const [appointments, setAppointments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [tags, setTags] = useState([]);
  const [users, setUsers] = useState([]);

  const {
    user: { id: currentUserId },
  } = useAuth();

  const addAppointment = (appointment) =>
    setAppointments((prev) => [...prev, appointment]);

  const deleteAppointment = (appointmentId) =>
    setAppointments((prev) =>
      prev.filter((appointment) => appointment.id !== appointmentId)
    );

  const updateAppointment = (appointment) =>
    setAppointments((prev) =>
      prev.map((oldAppointment) =>
        oldAppointment.id === appointment.id ? appointment : oldAppointment
      )
    );

  useEffect(() => {
    const fetchCalendar = async () => {
      setIsLoading(true);

      try {
        const [appointmentsResponse, tagsResponse, usersResponse] = await Promise.all([
          apiFetchAppointments(),
          apiFetchTags('Appointment'),
          apiFetchUsers(),
        ]);

        const [appointments, tags, users] = await Promise.all([
          deserializer.deserialize(appointmentsResponse),
          deserializer.deserialize(tagsResponse),
          deserializer.deserialize(usersResponse),
        ]);

        setAppointments(appointments);
        setTags(tags);
        setUsers(withCurrentUserFirst({ currentUserId, users }));
      } catch (error) {
        toast.error(error.message);
        Honeybadger.notify(error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCalendar();
  }, [currentUserId]);

  const contextValue = useMemo(
    () => ({
      addAppointment,
      appointments,
      deleteAppointment,
      isLoading,
      tags,
      updateAppointment,
      users,
    }),
    [appointments, isLoading, tags, users]
  );

  return (
    <CalendarContext.Provider value={contextValue}>{children}</CalendarContext.Provider>
  );
};

CalendarProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default CalendarProvider;
